import { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import { getAchievementCsvLayout, exportAchievementCsvOutput } from 'api/attendCSVOutput';
import { OptionType } from 'components/atoms/Select';
import moment from 'moment';
import useToastNotification from 'hooks/useToastNotification';
import { useEmploymentTypeOptions, useStaffAccessableOptions } from 'hooks/useEmploymentOptions';
import { useOrgTreesByOrgCSVCode } from 'hooks/useOrgTreesOptions';
import { getEmploymentOptions, getStaffAccessableOptions } from 'api/timeRecalculation';
import { getSelectListDateRange } from 'api/employment';

const { errorNotification } = useToastNotification();
export const useAttendCSVOutputForm = () => {
  // Range Time
  const startOfMonth = moment()
    .startOf('month')
    .format('YYYY-MM-DD');
  const endOfMonth = moment()
    .endOf('month')
    .format('YYYY-MM-DD');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [layoutIdOptions, setLayoutIdOptions] = useState<Array<OptionType>>([]);
  let orgTreesOptions: Array<any> = [];
  const [employmentOptions, setEmploymentOptions] = useState<Array<OptionType>>([]);
  const [staffOptions, setStaffOptions] = useState<Array<OptionType>>([]);
 
  const onSubmit = async (values: any) => {
    if(!values.orgCode){
      errorNotification('組織を選択してください。');
      return;
    }

    const submitObj = {
      loginStaffCode: values.loginStaffCode,
      targetDateFrom: moment(formik.values.targetDateFrom).startOf('month').format('YYYY-MM-DD'),
      targetDateTo: moment(formik.values.targetDateTo).endOf('month').format('YYYY-MM-DD'), // get last date month
      orgCode: values.orgCode,
      employmentId: values.employmentId,
      staffCode: values.staffCode === 'all' ? '' : values.staffCode,
      layoutId: values.layoutId
    };
    let orgName = orgTreesOptions.filter((org: any) => {
      return org.value === values.orgCode
    })[0].orgName;

    let layoutName = layoutIdOptions.filter((layout: any) => {
      return layout.value === values.layoutId
    })[0].label;

    const nameFile = `${layoutName}_${moment(values.targetDateFrom).format('YYYYMM')}_${moment(values.targetDateTo).format('YYYYMM')}_${orgName}.csv`;
    setIsLoading(true);

    try {
      exportAchievementCsvOutput(submitObj, nameFile).then((response: any) => {
        setIsLoading(false);
      }).catch((error) => {
        setIsLoading(false);
        if(error.response && error.response.data){
          errorNotification(error.response.data);
        }else{
          errorNotification('サーバー側でエラーが発生しました。');
        }        
      })
    } catch (error) {
      errorNotification('サーバー側でエラーが発生しました。');
      setIsLoading(false);
      }
    };

  const formik = useFormik({
    initialValues: {
      loginStaffCode: sessionStorage.getItem('loginUser.staffCode') || '',
      targetDateFrom: new Date(),
      targetDateTo: new Date(),
      orgCode: sessionStorage.getItem('loginUser.orgCode'),
      employmentId: null,
      staffCode: 'all',
      layoutId: ''
    },
    onSubmit,
  });

  orgTreesOptions = useOrgTreesByOrgCSVCode(formik.values.targetDateFrom, formik.values.targetDateTo);

  useEffect(() => {
    if (formik.values.targetDateFrom === null || formik.values.targetDateTo === null) return;
    setIsLoading(true);
    const dateFrom = moment(formik.values.targetDateFrom).startOf('month').format('YYYY-MM-DD');
    const dateTo = moment(formik.values.targetDateTo).endOf("month").format('YYYY-MM-DD');
    getSelectListDateRange(dateFrom, dateTo).then((trees: any) => {
      const employment = trees.map((tree: any) => ({
        value: tree.employmentId,
        label: `${tree.employmentCode} ${tree.employmentName}`,
      }));
      employment.unshift({
        value: '',
        label: '-',
      });
      setEmploymentOptions(employment);
      setIsLoading(false);
    });
  }, [formik.values.targetDateFrom, formik.values.targetDateTo]);

  useEffect(() => {
    if (formik.values.targetDateFrom === null || formik.values.targetDateTo === null) return;
    setIsLoading(true);
    const dateFrom = moment(formik.values.targetDateFrom).startOf('month').format('YYYY-MM-DD');
    const dateTo = moment(formik.values.targetDateTo).endOf("month").format('YYYY-MM-DD');
    const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || "";
    const addAllItem: boolean = true;
    getStaffAccessableOptions(loginStaffCode, formik.values.orgCode || 'all', dateFrom, dateTo, formik.values.employmentId || "", addAllItem).then((trees) => {
      const staffOptions = trees.map((tree) => ({
        value: tree.staffCode,
        label: `${tree.staffCode} ${tree.staffName}`,
      }));
      setStaffOptions(staffOptions);
      setIsLoading(false);
    });
  }, [formik.values.orgCode, formik.values.targetDateFrom, formik.values.targetDateTo, formik.values.employmentId, employmentOptions]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      getAchievementCsvLayout().then((response: any) => {
        setLayoutIdOptions(response.map((item: any) => ({
          value: item.layoutId,
          label: item.layoutName
        })))
        formik.setFieldValue('layoutId', response[0].layoutId);
      }).catch((error: any) => {
        errorNotification('サーバー側でエラーが発生しました。');  
      }).finally(()=>{
        setIsLoading(false);
      }); 
    } catch (error) {
      errorNotification('サーバー側でエラーが発生しました。');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if(orgTreesOptions.filter((org:any) => (org.value === formik.values.orgCode)).length === 0){
      formik.setFieldValue('orgCode', 'all');
    }
  }, [formik.values.targetDateFrom, formik.values.targetDateTo])


  useEffect(() => {
    if(staffOptions.filter((staff:any) => (staff.value === formik.values.staffCode)).length === 0){
      formik.setFieldValue('staffCode', 'all');
    }
  }, [staffOptions])
  return {
    isLoading,
    orgTreesOptions,
    staffOptions,
    formik,
    employmentOptions,
    layoutIdOptions
  };
};

export default useAttendCSVOutputForm;
